import DigiobcanApi from '../../../api/DigiobcanApi';
import { entityId } from '../../../lib/ids';
import { useM } from '../../../lib/query';
import { getVotesDeclension, toastError, __ } from '../../../utils';
import NorthIcon from '@mui/icons-material/North';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { useParams } from 'react-router';

type Props = {
  /** Koľko žetónov má občan ešte k dispozícii vo svojom fonde. */
  userAvailableVoteTokens: number;
  /** Koľko hlasov už občan dal tomuto podnetu. Nenulové = tlačidlo je zapnuté. */
  userActiveVoteTokens: number;
  /** Počet hlasov na podnete cez všetky účty. */
  globalVoteTokens: number;
  isLoading: boolean;
  onSuccess?: (isVoting: boolean) => void;
  onClickUnregisteredUser?: () => void;
  fullWidth?: boolean;
  tooltip?: string;
  /**
   * Podnet, o ktorý ide. Bez neho sa berie z adresy - tak to funguje na
   * stránke `/podnety/:reportId`. Panel na hlavnej stránke žiadny taký
   * parameter nemá, lebo detail v ňom nie je route, takže identifikátor podá.
   */
  reportId?: string;
  /**
   * `inline` je pôvodný tvar: tlačidlo „Hlasovať" a vedľa neho políčko
   * s počtom. `panel` je jedno tlačidlo na plnú šírku („Podporiť · 58"), lebo
   * v paneli hlavnej stránky je to jediná akcia a dve políčka vedľa seba by
   * v 390 px pôsobili ako formulár.
   */
  variant?: 'inline' | 'panel';
};

const VoteButton: React.FC<Props> = ({
  userAvailableVoteTokens,
  userActiveVoteTokens,
  globalVoteTokens,
  isLoading,
  onSuccess,
  onClickUnregisteredUser,
  tooltip,
  fullWidth = false,
  reportId: reportIdProp,
  variant = 'inline',
}) => {
  const { reportId: reportIdParam } = useParams() as {
    reportId?: string;
  };
  const reportId = reportIdProp ?? entityId(reportIdParam ?? '', 'report');
  const { authUser } = useAuth();

  const [marked, setMarked] = React.useState(false);

  React.useEffect(() => {
    if (userActiveVoteTokens) {
      setMarked(true);
    } else {
      setMarked(false);
    }
  }, [userActiveVoteTokens]);

  const meVoteTokenRevokeMutation = useM(
    () => {
      return DigiobcanApi.meVoteTokenRevoke(undefined, {
        count: 1,
        target: reportId,
      });
    },
    {
      onSuccess: () => {
        onSuccess?.(false); // false = revoking vote
      },
      onError: () => {
        setMarked(true);
        toastError(__('Hlas sa nepodarilo odvolať. Skúste to znova.'));
      },
    },
  );

  const meVoteTokenMutation = useM(
    () => {
      return DigiobcanApi.meVoteToken(undefined, {
        count: 1,
        target: reportId,
      });
    },
    {
      onSuccess: () => {
        onSuccess?.(true); // true = casting vote
      },
      // Tlačidlo sa prepína optimisticky, takže bez hlášky by odmietnutý hlas
      // vyzeral ako preblikanie - a presne tak vyzeral, kým sa `marked` len
      // ticho vrátilo späť.
      onError: () => {
        setMarked(false);
        toastError(__('Podnet sa nepodarilo podporiť. Skúste to znova.'));
      },
    },
  );

  if (isLoading) {
    if (variant === 'panel') {
      return <Skeleton variant='rounded' width='100%' height={44} />;
    }

    return (
      <Stack
        direction={'row'}
        spacing={0.5}
        width={fullWidth ? '100%' : 'auto'}
      >
        <Skeleton width={fullWidth ? '75%' : 80} height={25} />
        <Skeleton width={fullWidth ? '25%' : 25} height={25} />
      </Stack>
    );
  }

  const loggedUserWithNoTokens =
    userAvailableVoteTokens === 0 && !marked && !!authUser;

  const mutationsPending =
    meVoteTokenMutation.isPending || meVoteTokenRevokeMutation.isPending;

  const disabled = loggedUserWithNoTokens || mutationsPending;

  const toggle = async () => {
    if (!authUser) {
      onClickUnregisteredUser?.();
      return;
    }
    if (marked) {
      setMarked(false);
      await meVoteTokenRevokeMutation.mutateAsync();
    } else {
      setMarked(true);
      await meVoteTokenMutation.mutateAsync();
    }
  };

  // Panelový tvar: jedno tlačidlo na plnú šírku, počet v jeho texte. Logika je
  // tá istá, mení sa len to, ako sa to isté ponúkne v úzkom stĺpci.
  if (variant === 'panel') {
    return (
      <Button
        variant={marked ? 'primary' : 'outline'}
        // 44 px zo zadania - je to jediná primárna akcia detailu a na mobile
        // sa do nej trafí prstom. Vedľa nej stojí rovnako vysoké zdieľanie.
        size='large'
        fullWidth
        disabled={disabled}
        onClick={toggle}
        startIcon={<NorthIcon sx={{ fontSize: 15 }} />}
      >
        {`${marked ? __('Podporené') : __('Podporiť')} · ${globalVoteTokens}`}
      </Button>
    );
  }

  const button = (
    <Button
      color='primary'
      disabled={disabled}
      disableElevation
      onClick={toggle}
      variant={marked ? 'contained' : 'outlined'}
      sx={{
        height: 25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ...(fullWidth && {
          flex: '3 1 75%',
          minWidth: 0,
          width: '75%',
        }),
        ...(marked && {
          '&:hover': {
            backgroundColor: 'primary.main',
          },
        }),
      }}
    >
      <Typography variant='caption' fontWeight={600}>
        {marked ? __('Hlasoval som') : __('Hlasovať')}
      </Typography>
    </Button>
  );

  return (
    <Tooltip
      title={
        loggedUserWithNoTokens && !mutationsPending
          ? 'Nemáte žetóny na hlasovanie'
          : ''
      }
    >
      <Stack
        direction={'row'}
        spacing={0.5}
        width={fullWidth ? '100%' : 'auto'}
        sx={{
          ...(fullWidth && { width: '100%' }),
        }}
      >
        {/* Na mobile schovame tooltip */}
        {fullWidth ? button : <Tooltip title={tooltip}>{button}</Tooltip>}
        <Box
          color={'gray'}
          border={'1px solid lightgray'}
          px={0.5}
          height={25}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          borderRadius={1}
          sx={{
            ...(fullWidth && {
              flex: '1 1 25%',
              minWidth: 0,
              width: '25%',
            }),
          }}
        >
          <Typography variant='caption' color='black'>
            {globalVoteTokens} {getVotesDeclension(globalVoteTokens)}
          </Typography>
        </Box>
      </Stack>
    </Tooltip>
  );
};

export default VoteButton;
